<template>
  <div class="gamification-campaigns-page">
    <div class="page-header">
      <div class="header-top">
        <div>
          <h2 class="page-title mb-25">Gamificação - Campanhas</h2>
          <p class="page-subtitle text-muted mb-0">Gerencie campanhas de gamificação para sua equipe de vendas</p>
        </div>
        <div class="header-actions">
          <b-button variant="primary" size="sm" @click="openCreateModal">
            <feather-icon icon="PlusIcon" size="14" class="mr-25" />
            Nova Campanha
          </b-button>
        </div>
      </div>
    </div>

    <div class="stats-row">
      <b-card no-body class="stat-card">
        <div class="stat-card-body">
          <b-avatar size="42" variant="light-primary" rounded>
            <feather-icon icon="FlagIcon" size="18" />
          </b-avatar>
          <div class="stat-info">
            <span class="stat-value">{{ stats.total }}</span>
            <span class="stat-label text-muted">Total Campanhas</span>
          </div>
        </div>
      </b-card>
      <b-card no-body class="stat-card">
        <div class="stat-card-body">
          <b-avatar size="42" variant="light-success" rounded>
            <feather-icon icon="ZapIcon" size="18" />
          </b-avatar>
          <div class="stat-info">
            <span class="stat-value">{{ stats.ativas }}</span>
            <span class="stat-label text-muted">Ativas</span>
          </div>
        </div>
      </b-card>
      <b-card no-body class="stat-card">
        <div class="stat-card-body">
          <b-avatar size="42" variant="light-info" rounded>
            <feather-icon icon="UsersIcon" size="18" />
          </b-avatar>
          <div class="stat-info">
            <span class="stat-value">{{ stats.participantes }}</span>
            <span class="stat-label text-muted">Participantes</span>
          </div>
        </div>
      </b-card>
    </div>

    <b-card no-body class="filters-card mb-1">
      <div class="filters-bar">
        <b-form-group label="Status" label-size="sm" class="mb-0 filter-status">
          <b-form-select v-model="filterStatus" size="sm" @change="loadCampaigns">
            <option value="">Todas</option>
            <option value="rascunho">Rascunho</option>
            <option value="ativa">Ativa</option>
            <option value="inativa">Inativa</option>
            <option value="arquivada">Arquivada</option>
          </b-form-select>
        </b-form-group>
        <b-button variant="outline-secondary" size="sm" @click="loadCampaigns" class="ml-50">
          <feather-icon icon="RefreshCwIcon" size="14" />
        </b-button>
      </div>
    </b-card>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" />
      <p class="mt-1 text-muted">Carregando campanhas...</p>
    </div>

    <b-card v-else-if="campaigns.length === 0">
      <div class="text-center py-4">
        <feather-icon icon="AwardIcon" size="48" class="text-muted mb-1" />
        <h5 class="text-muted">Nenhuma campanha encontrada</h5>
        <p class="text-muted small">Crie sua primeira campanha de gamificação.</p>
        <b-button variant="primary" size="sm" @click="openCreateModal">
          <feather-icon icon="PlusIcon" size="14" class="mr-25" />
          Nova Campanha
        </b-button>
      </div>
    </b-card>

    <b-card v-else no-body>
      <b-table
        :items="campaigns"
        :fields="tableFields"
        responsive
        hover
        show-empty
        empty-text="Nenhuma campanha encontrada"
        class="mb-0"
        @row-clicked="onRowClick"
      >
        <template #cell(nome)="{ item }">
          <div>
            <strong>{{ item.nome }}</strong>
            <small v-if="item.descricao" class="text-muted d-block">{{ truncate(item.descricao, 60) }}</small>
          </div>
        </template>
        <template #cell(periodo)="{ item }">
          <span class="small text-muted">
            {{ formatDate(item.data_inicio) }} - {{ formatDate(item.data_fim) }}
          </span>
        </template>
        <template #cell(status)="{ item }">
          <b-badge :variant="statusVariant(item.status)" pill>{{ statusLabel(item.status) }}</b-badge>
        </template>
        <template #cell(participantes)="{ item }">
          <span>{{ item.total_participants || 0 }}</span>
        </template>
        <template #cell(acoes)="{ item }">
          <div class="actions-cell" @click.stop>
            <b-button variant="flat-primary" size="sm" @click="editCampaign(item)" title="Editar">
              <feather-icon icon="EditIcon" size="14" />
            </b-button>
            <b-button variant="flat-info" size="sm" @click="duplicateCampaign(item)" title="Duplicar">
              <feather-icon icon="CopyIcon" size="14" />
            </b-button>
            <b-button variant="flat-danger" size="sm" @click="confirmDelete(item)" title="Excluir">
              <feather-icon icon="Trash2Icon" size="14" />
            </b-button>
          </div>
        </template>
      </b-table>
    </b-card>

    <b-modal
      v-model="showModal"
      :title="editingCampaign ? 'Editar Campanha' : 'Nova Campanha'"
      ok-title="Salvar"
      cancel-title="Cancelar"
      @ok="saveCampaign"
      :ok-disabled="!form.nome"
      centered
      size="lg"
      no-close-on-backdrop
    >
      <b-row>
        <b-col md="8">
          <b-form-group label="Nome *" label-for="gam-nome">
            <b-form-input id="gam-nome" v-model="form.nome" placeholder="Nome da campanha" />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Status" label-for="gam-status">
            <b-form-select id="gam-status" v-model="form.status">
              <option value="rascunho">Rascunho</option>
              <option value="ativa">Ativa</option>
              <option value="inativa">Inativa</option>
              <option value="arquivada">Arquivada</option>
            </b-form-select>
          </b-form-group>
        </b-col>
      </b-row>
      <b-form-group label="Descrição" label-for="gam-desc">
        <b-form-textarea id="gam-desc" v-model="form.descricao" rows="2" placeholder="Descrição da campanha (opcional)" />
      </b-form-group>
      <b-row>
        <b-col md="6">
          <b-form-group label="Data Início" label-for="gam-inicio">
            <b-form-input id="gam-inicio" v-model="form.data_inicio" type="date" />
          </b-form-group>
        </b-col>
        <b-col md="6">
          <b-form-group label="Data Fim" label-for="gam-fim">
            <b-form-input id="gam-fim" v-model="form.data_fim" type="date" />
          </b-form-group>
        </b-col>
      </b-row>
      <hr />
      <h6 class="mb-75">Prêmios</h6>
      <b-row>
        <b-col md="4">
          <b-form-group label="1º Lugar - Valor" label-for="gam-p1v">
            <b-form-input id="gam-p1v" v-model="form.premio_1_valor" type="number" placeholder="0.00" />
          </b-form-group>
        </b-col>
        <b-col md="8">
          <b-form-group label="1º Lugar - Descrição" label-for="gam-p1d">
            <b-form-input id="gam-p1d" v-model="form.premio_1_descricao" placeholder="Ex: Viagem para Cancún" />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="4">
          <b-form-group label="2º Lugar - Valor" label-for="gam-p2v">
            <b-form-input id="gam-p2v" v-model="form.premio_2_valor" type="number" placeholder="0.00" />
          </b-form-group>
        </b-col>
        <b-col md="8">
          <b-form-group label="2º Lugar - Descrição" label-for="gam-p2d">
            <b-form-input id="gam-p2d" v-model="form.premio_2_descricao" placeholder="Ex: Smartphone" />
          </b-form-group>
        </b-col>
      </b-row>
    </b-modal>

    <b-modal
      v-model="showDeleteModal"
      title="Excluir Campanha"
      ok-title="Excluir"
      ok-variant="danger"
      cancel-title="Cancelar"
      centered
      @ok="deleteCampaign"
    >
      <p>Tem certeza que deseja excluir a campanha <strong>{{ deletingCampaign ? deletingCampaign.nome : '' }}</strong>?</p>
      <p class="text-muted small">Esta ação não pode ser desfeita.</p>
    </b-modal>
  </div>
</template>

<script>
import gamificationClient from '@/api/CRM/gamification.client';
import {
  BCard, BTable, BButton, BBadge, BModal, BFormInput, BFormGroup,
  BFormSelect, BFormTextarea, BSpinner, BRow, BCol, BAvatar,
} from 'bootstrap-vue';
import ToastificationContent from '@/@core/components/toastification/ToastificationContent.vue';

export default {
  name: 'GamificationCampaigns',
  components: {
    BCard, BTable, BButton, BBadge, BModal, BFormInput, BFormGroup,
    BFormSelect, BFormTextarea, BSpinner, BRow, BCol, BAvatar,
  },
  data() {
    return {
      loading: false,
      campaigns: [],
      filterStatus: '',
      stats: { total: 0, ativas: 0, participantes: 0 },
      showModal: false,
      showDeleteModal: false,
      editingCampaign: null,
      deletingCampaign: null,
      form: this.emptyForm(),
      tableFields: [
        { key: 'nome', label: 'Nome', sortable: true },
        { key: 'periodo', label: 'Período' },
        { key: 'status', label: 'Status', class: 'text-center' },
        { key: 'participantes', label: 'Participantes', class: 'text-center' },
        { key: 'acoes', label: 'Ações', class: 'text-center', thStyle: 'width: 140px' },
      ],
    };
  },
  created() {
    this.loadCampaigns();
  },
  methods: {
    emptyForm() {
      return {
        nome: '',
        descricao: '',
        data_inicio: '',
        data_fim: '',
        status: 'rascunho',
        premio_1_valor: '',
        premio_1_descricao: '',
        premio_2_valor: '',
        premio_2_descricao: '',
      };
    },
    async loadCampaigns() {
      this.loading = true;
      try {
        const params = {};
        if (this.filterStatus) params.status = this.filterStatus;
        const result = await gamificationClient.listCampaigns(params);
        const payload = result.data || result;
        this.campaigns = payload.rows || payload.items || (Array.isArray(payload) ? payload : []);
        if (Array.isArray(this.campaigns)) {
          this.stats.total = payload.total || this.campaigns.length;
          this.stats.ativas = this.campaigns.filter(c => c.status === 'ativa').length;
          this.stats.participantes = this.campaigns.reduce((sum, c) => sum + (c.total_participants || 0), 0);
        }
      } catch (error) {
        console.error('Erro ao carregar campanhas:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao carregar campanhas',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.loading = false;
      }
    },
    openCreateModal() {
      this.editingCampaign = null;
      this.form = this.emptyForm();
      this.showModal = true;
    },
    editCampaign(item) {
      this.editingCampaign = item;
      this.form = {
        nome: item.nome || '',
        descricao: item.descricao || '',
        data_inicio: item.data_inicio ? item.data_inicio.substring(0, 10) : '',
        data_fim: item.data_fim ? item.data_fim.substring(0, 10) : '',
        status: item.status || 'rascunho',
        premio_1_valor: item.premio_1_valor || '',
        premio_1_descricao: item.premio_1_descricao || '',
        premio_2_valor: item.premio_2_valor || '',
        premio_2_descricao: item.premio_2_descricao || '',
      };
      this.showModal = true;
    },
    async saveCampaign(e) {
      e.preventDefault();
      try {
        if (this.editingCampaign) {
          await gamificationClient.updateCampaign(this.editingCampaign.id, this.form);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Campanha atualizada com sucesso',
              icon: 'CheckIcon',
              variant: 'success',
            },
          });
        } else {
          await gamificationClient.createCampaign(this.form);
          this.$toast({
            component: ToastificationContent,
            props: {
              title: 'Campanha criada com sucesso',
              icon: 'CheckIcon',
              variant: 'success',
            },
          });
        }
        this.showModal = false;
        this.loadCampaigns();
      } catch (error) {
        const msg = error.response?.data?.message || 'Erro ao salvar campanha';
        this.$toast({
          component: ToastificationContent,
          props: {
            title: msg,
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    async duplicateCampaign(item) {
      try {
        await gamificationClient.duplicateCampaign(item.id);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Campanha duplicada com sucesso',
            icon: 'CopyIcon',
            variant: 'success',
          },
        });
        this.loadCampaigns();
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao duplicar campanha',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    confirmDelete(item) {
      this.deletingCampaign = item;
      this.showDeleteModal = true;
    },
    async deleteCampaign() {
      if (!this.deletingCampaign) return;
      try {
        await gamificationClient.deleteCampaign(this.deletingCampaign.id);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Campanha excluída com sucesso',
            icon: 'TrashIcon',
            variant: 'success',
          },
        });
        this.loadCampaigns();
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro ao excluir campanha',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      }
    },
    onRowClick(item) {
      this.$router.push({ name: 'gamification-campaign-detail', params: { id: item.id } });
    },
    statusVariant(s) {
      const map = {
        ativa: 'light-success',
        rascunho: 'light-secondary',
        inativa: 'light-warning',
        arquivada: 'light-dark',
      };
      return map[s] || 'light-secondary';
    },
    statusLabel(s) {
      const map = { ativa: 'Ativa', rascunho: 'Rascunho', inativa: 'Inativa', arquivada: 'Arquivada' };
      return map[s] || s;
    },
    formatDate(d) {
      if (!d) return '-';
      return new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
    },
    truncate(text, len) {
      if (!text) return '';
      return text.length > len ? text.substring(0, len) + '...' : text;
    },
  },
};
</script>

<style scoped>
.gamification-campaigns-page {
  padding: 0.75rem;
}

.page-header {
  margin-bottom: 1.25rem;
}

.header-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1rem;
}

.page-title {
  font-size: 1.5rem;
  font-weight: 600;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.stat-card {
  margin-bottom: 0;
}

.stat-card-body {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 1rem 1.25rem;
}

.stat-info {
  display: flex;
  flex-direction: column;
}

.stat-value {
  font-size: 1.35rem;
  font-weight: 700;
  line-height: 1.2;
}

.stat-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.filters-bar {
  padding: 0.875rem 1.25rem;
  display: flex;
  align-items: flex-end;
  gap: 0.5rem;
}

.filter-status {
  min-width: 160px;
}

.actions-cell {
  display: flex;
  justify-content: center;
  gap: 0.15rem;
}

@media (max-width: 768px) {
  .stats-row {
    grid-template-columns: 1fr;
  }
  .header-top {
    flex-direction: column;
  }
}
</style>
